/**
 * Date Utility Functions
 * Centralized date formatting and utility functions
 */

import { DATE_FORMATS } from '@/constants/common'

// Date formatting functions
export const formatDate = (date, format = DATE_FORMATS.SHORT, options: Record<string, any> = {}) => {
  if (!date) return 'N/A'

  try {
    const dateObj = new Date(date)
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date'
    }

    const {
      locale = 'en-US',
      timeZone = 'UTC',
      ...formatOptions
    } = options

    // Handle different format types
    switch (format) {
      case DATE_FORMATS.SHORT:
        return dateObj.toLocaleDateString(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone,
          ...formatOptions
        })

      case DATE_FORMATS.LONG:
        return dateObj.toLocaleDateString(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone,
          ...formatOptions
        })

      case DATE_FORMATS.DATETIME:
        return dateObj.toLocaleString(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone,
          ...formatOptions
        })

      case DATE_FORMATS.TIME:
        return dateObj.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          timeZone,
          ...formatOptions
        })

      case DATE_FORMATS.ISO:
        return dateObj.toISOString()

      default:
        // Custom format using Intl.DateTimeFormat
        return new Intl.DateTimeFormat(locale, {
          ...formatOptions,
          timeZone
        }).format(dateObj)
    }
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Invalid Date'
  }
}

// Relative time formatting
export const formatRelativeTime = (date, options: Record<string, any> = {}) => {
  if (!date) return 'N/A'

  try {
    const dateObj = new Date(date)
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date'
    }

    const {
      locale = 'en-US',
      timeZone = 'UTC',
      ...formatOptions
    } = options

    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

    // Use Intl.RelativeTimeFormat for better localization
    const rtf = new Intl.RelativeTimeFormat(locale, {
      numeric: 'auto',
      ...formatOptions
    })

    // Calculate relative time
    if (diffInSeconds < 60) {
      return rtf.format(-diffInSeconds, 'second')
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return rtf.format(-minutes, 'minute')
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return rtf.format(-hours, 'hour')
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400)
      return rtf.format(-days, 'day')
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000)
      return rtf.format(-months, 'month')
    } else {
      const years = Math.floor(diffInSeconds / 31536000)
      return rtf.format(-years, 'year')
    }
  } catch (error) {
    console.error('Error formatting relative time:', error)
    return 'Invalid Date'
  }
}

// Date validation
export const isValidDate = (date) => {
  if (!date) return false
  
  const dateObj = new Date(date)
  return !isNaN(dateObj.getTime())
}

export const isDateInRange = (date, startDate, endDate) => {
  if (!isValidDate(date) || !isValidDate(startDate) || !isValidDate(endDate)) {
    return false
  }

  const dateObj = new Date(date)
  const start = new Date(startDate)
  const end = new Date(endDate)

  return dateObj >= start && dateObj <= end
}

export const isDateToday = (date) => {
  if (!isValidDate(date)) return false

  const dateObj = new Date(date)
  const today = new Date()

  return dateObj.toDateString() === today.toDateString()
}

export const isDateYesterday = (date) => {
  if (!isValidDate(date)) return false

  const dateObj = new Date(date)
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)

  return dateObj.toDateString() === yesterday.toDateString()
}

export const isDateThisWeek = (date) => {
  if (!isValidDate(date)) return false

  const dateObj = new Date(date)
  const today = new Date()
  const startOfWeek = new Date(today)
  startOfWeek.setDate(today.getDate() - today.getDay())
  const endOfWeek = new Date(startOfWeek)
  endOfWeek.setDate(startOfWeek.getDate() + 6)

  return dateObj >= startOfWeek && dateObj <= endOfWeek
}

export const isDateThisMonth = (date) => {
  if (!isValidDate(date)) return false

  const dateObj = new Date(date)
  const today = new Date()

  return dateObj.getMonth() === today.getMonth() && 
         dateObj.getFullYear() === today.getFullYear()
}

export const isDateThisYear = (date) => {
  if (!isValidDate(date)) return false

  const dateObj = new Date(date)
  const today = new Date()

  return dateObj.getFullYear() === today.getFullYear()
}

// Date manipulation
export const addDays = (date, days) => {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

export const addMonths = (date, months) => {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

export const addYears = (date, years) => {
  const result = new Date(date)
  result.setFullYear(result.getFullYear() + years)
  return result
}

export const subtractDays = (date, days) => {
  return addDays(date, -days)
}

export const subtractMonths = (date, months) => {
  return addMonths(date, -months)
}

export const subtractYears = (date, years) => {
  return addYears(date, -years)
}

// Date comparison
export const isAfter = (date1, date2) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return false
  return new Date(date1) > new Date(date2)
}

export const isBefore = (date1, date2) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return false
  return new Date(date1) < new Date(date2)
}

export const isSameDay = (date1, date2) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return false
  return new Date(date1).toDateString() === new Date(date2).toDateString()
}

export const isSameMonth = (date1, date2) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return false
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  return d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear()
}

export const isSameYear = (date1, date2) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return false
  return new Date(date1).getFullYear() === new Date(date2).getFullYear()
}

// Date range utilities
export const getDateRange = (startDate, endDate) => {
  if (!isValidDate(startDate) || !isValidDate(endDate)) return []

  const dates = []
  const start = new Date(startDate)
  const end = new Date(endDate)

  for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
    dates.push(new Date(date))
  }

  return dates
}

export const getDaysBetween = (startDate, endDate) => {
  if (!isValidDate(startDate) || !isValidDate(endDate)) return 0

  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = Math.abs(end.getTime() - start.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

export const getMonthsBetween = (startDate, endDate) => {
  if (!isValidDate(startDate) || !isValidDate(endDate)) return 0

  const start = new Date(startDate)
  const end = new Date(endDate)
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
}

export const getYearsBetween = (startDate, endDate) => {
  if (!isValidDate(startDate) || !isValidDate(endDate)) return 0

  const start = new Date(startDate)
  const end = new Date(endDate)
  return end.getFullYear() - start.getFullYear()
}

// Date parsing
export const parseDate = (dateString, format = null) => {
  if (!dateString) return null

  try {
    // Try parsing as ISO string first
    if (dateString.includes('T') || dateString.includes('Z')) {
      return new Date(dateString)
    }

    // Try parsing with different formats
    const formats = [
      'YYYY-MM-DD',
      'MM/DD/YYYY',
      'DD/MM/YYYY',
      'MM-DD-YYYY',
      'DD-MM-YYYY'
    ]

    for (const fmt of formats) {
      const parsed = parseDateWithFormat(dateString, fmt)
      if (parsed && isValidDate(parsed)) {
        return parsed
      }
    }

    // Fallback to default parsing
    return new Date(dateString)
  } catch (error) {
    console.error('Error parsing date:', error)
    return null
  }
}

const parseDateWithFormat = (dateString, format) => {
  // Simple date parsing based on format
  // This is a basic implementation - for production, consider using a library like date-fns
  const parts = dateString.split(/[-\/]/)
  const formatParts = format.split(/[-\/]/)

  if (parts.length !== formatParts.length) return null

  let year, month, day

  formatParts.forEach((part, index) => {
    const value = parseInt(parts[index])
    switch (part) {
      case 'YYYY':
        year = value
        break
      case 'MM':
        month = value - 1 // JavaScript months are 0-indexed
        break
      case 'DD':
        day = value
        break
    }
  })

  if (year && month !== undefined && day) {
    return new Date(year, month, day)
  }

  return null
}

// Date formatting presets
export const datePresets = {
  // Common date formats
  short: (date) => formatDate(date, DATE_FORMATS.SHORT),
  long: (date) => formatDate(date, DATE_FORMATS.LONG),
  datetime: (date) => formatDate(date, DATE_FORMATS.DATETIME),
  time: (date) => formatDate(date, DATE_FORMATS.TIME),
  iso: (date) => formatDate(date, DATE_FORMATS.ISO),
  
  // Relative time
  relative: (date) => formatRelativeTime(date),
  
  // Smart formatting (shows relative time for recent dates, absolute for older)
  smart: (date) => {
    if (!isValidDate(date)) return 'N/A'
    
    const dateObj = new Date(date)
    const now = new Date()
    const diffInDays = Math.floor((now.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24))
    
    if (diffInDays === 0) {
      return 'Today'
    } else if (diffInDays === 1) {
      return 'Yesterday'
    } else if (diffInDays < 7) {
      return formatRelativeTime(date)
    } else if (isDateThisYear(date)) {
      return formatDate(date, DATE_FORMATS.SHORT)
    } else {
      return formatDate(date, DATE_FORMATS.LONG)
    }
  }
}

// Date validation rules
export const dateValidationRules = {
  required: (value) => {
    if (!value) return 'Date is required'
    if (!isValidDate(value)) return 'Please enter a valid date'
    return null
  },
  
  minDate: (minDate) => (value) => {
    if (!value) return null
    if (!isValidDate(value)) return 'Please enter a valid date'
    if (isBefore(value, minDate)) return `Date must be after ${formatDate(minDate)}`
    return null
  },
  
  maxDate: (maxDate) => (value) => {
    if (!value) return null
    if (!isValidDate(value)) return 'Please enter a valid date'
    if (isAfter(value, maxDate)) return `Date must be before ${formatDate(maxDate)}`
    return null
  },
  
  dateRange: (startDate, endDate) => (value) => {
    if (!value) return null
    if (!isValidDate(value)) return 'Please enter a valid date'
    if (!isDateInRange(value, startDate, endDate)) {
      return `Date must be between ${formatDate(startDate)} and ${formatDate(endDate)}`
    }
    return null
  }
}
